import { createFileRoute, useNavigate } from '@tanstack/react-router';

import {
  applyStudioPatch,
  MetricStudioPage,
  toStudioLocation,
  validateStudioSearch,
} from '@/features/metric-studio';
import { isMarket, type Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  `/:market/securities/:symbol/valuation/:metricKey`
 *
 *  **四條路由共用同一個元件**（ROADMAP §6 交付項）。這個檔案 30 行，而且
 *  另外三條除了路徑以外**逐字相同**——那就是「一個元件換 8 頁」這句話的形狀。
 *
 *  ⚠️ 這個檔案裡沒有、也不准有任何 metric_key。目的地是 `$metricKey`，
 *  來源是 `GET /meta/metrics` 的 `href_template`（見 features/metric-studio/nav.ts）。
 *  驗收 #2 的 grep 就是在這幾個檔案裡找那兩個 metric key ——
 *  所以連**註解裡都不可以寫出它們**：grep 不區分程式碼與註解，
 *  一句「這裡不准出現 X」的註解會讓驗收自己失敗。
 * ========================================================================== */
export const Route = createFileRoute('/$market/securities/$symbol/valuation/$metricKey')({
  validateSearch: validateStudioSearch,
  component: ValuationStudioRoute,
});

function ValuationStudioRoute() {
  const { market: rawMarket, symbol, metricKey } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const market: Market = isMarket(rawMarket) ? rawMarket : 'tw';

  return (
    <MetricStudioPage
      location={toStudioLocation({ market, symbol, metricKey }, search)}
      onChange={(patch) => {
        void navigate({
          // `to: '.'` ＝ 同一條路由，只換 search。元件實例不變，React 不 remount，
          // `resetScroll: false` 讓 router 不要把頁面拉回頂端 —— 這兩件事合起來
          // 就是驗收 #5 的「不重新載入整頁、不重置捲軸位置」。
          to: '.',
          search: (prev: Record<string, unknown>) => applyStudioPatch(prev, patch),
          replace: true,
          resetScroll: false,
        });
      }}
    />
  );
}
